import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useAtom } from "jotai";
import { useCallback, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import { useForm } from "react-hook-form";
import ReactInputMask from "react-input-mask";
import { z } from "zod";

import { ActionButton } from "../../../fkc/components/shared/submit-button/submit-button.component";
import { updateFormioSubmission } from "../../../shared/api/yenta-api";
import { validatePhoneNumber } from "../../../shared/utils";
import {
  SMS_PRIVACY_POLICY_URL,
  SMS_TNC_URL,
  OFFER_TERMS_URL,
  SMS_EXTRA_ENTRIES,
  SMS_FORM_TITLE,
} from "../../restaurant-equippers.constants";
import {
  companyFormCompletedAtom,
  pagesAtom,
  smsCompletedAtom,
} from "../../restaurant-equippers.state";
import { Pages } from "../../restaurant-equippers.types";
import { FormHeader } from "../form-header/form-header.component";

type SMSFormData = z.infer<typeof formSchema>;

const formSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, { message: "Phone number is required" })
    .refine(validatePhoneNumber, {
      message: "Please enter a valid phone number",
    }),
  tacAccepted: z.literal<boolean>(true, {
    errorMap: () => ({
      message: "You must agree to receive SMS messages",
    }),
  }),
});

export function SMSForm(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [, setPage] = useAtom(pagesAtom);
  const [, setSmsCompleted] = useAtom(smsCompletedAtom);
  const [companyFormCompleted] = useAtom(companyFormCompletedAtom);
  const handleWidgetError = useErrorHandler();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SMSFormData>({
    resolver: zodResolver(formSchema),
  });

  const goToNextStep = useCallback(() => {
    if (companyFormCompleted) {
      setPage(Pages.CONFIRMATION);
    } else {
      setPage(Pages.COMPANY_FORM);
    }
  }, [companyFormCompleted, setPage]);

  const registerUser = useCallback(
    ({ phoneNumber }: SMSFormData): void => {
      setIsLoading(true);

      const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

      if (!submission) {
        throw new Error("No submission found");
      }

      const sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") || "";

      const patchData = {
        ...submission.data,
        smsMobilePhone: phoneNumber,
        smsTermsAndConditions: true,
      };

      updateFormioSubmission(
        sweepFormUrl,
        {
          is_cpl_in_flow: true,
          cpl_in_flow_extra_entries: SMS_EXTRA_ENTRIES,
          ...submission,
          data: patchData,
        },
        handleWidgetError
      )
        .then(window.theme.Sweepstake.prototype.storeSubmission)
        .then(() =>
          window.theme.LyticsService.landerQuizVariablesTracker({
            variableKey: "re_phone_number",
            variableValue: phoneNumber,
          })
        )
        .then(() =>
          window.theme.LyticsService.landerQuizVariablesTracker({
            variableKey: "phoneNumber",
            variableValue: phoneNumber,
          })
        )
        .then(() => setSmsCompleted(true))
        .then(goToNextStep)
        .catch(handleWidgetError)
        .finally(() => setIsLoading(false));
    },
    [handleWidgetError, goToNextStep, setSmsCompleted]
  );

  return (
    <>
      <FormHeader progress={50} />

      <div className="re__optin--content">
        <div className="re__optin--entries-badge">
          <span className="re__optin--entries-number">
            +{SMS_EXTRA_ENTRIES}
          </span>
          <span className="re__optin--entries-label">entries</span>
        </div>

        <div className="re__optin--question">{SMS_FORM_TITLE}</div>
      </div>

      <form className="re__optin--form" onSubmit={handleSubmit(registerUser)}>
        <div className="re__optin--input-container">
          <ReactInputMask
            className={`re__optin--input ${
              errors.phoneNumber ? "re__optin--input--error" : ""
            }`}
            mask="(999) 999-9999"
            placeholder="Phone number"
            aria-label="Phone Number"
            disabled={isLoading}
            {...register("phoneNumber")}
          />
          {errors.phoneNumber && (
            <div className="re__optin--error" aria-invalid="true">
              {errors.phoneNumber.message}
            </div>
          )}
        </div>

        <div className="re__optin--consent">
          <input
            id="re-terms-and-privacy"
            type="checkbox"
            defaultChecked
            {...register("tacAccepted")}
          />
          <label
            htmlFor="re-terms-and-privacy"
            className="re__optin--consent-text"
          >
            By submitting this form and providing your cell phone number, you
            agree to receive recurring automated promotional and personalized
            marketing text messages as well as sales calls (e.g. cart reminders)
            at this number from Restaurant Equippers. Consent is not a condition
            of any purchase. Reply HELP for help and STOP to cancel. Msg
            frequency varies. Msg & data rates may apply. View{" "}
            <a
              href={SMS_TNC_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href={SMS_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            . Offer terms available{" "}
            <a href={OFFER_TERMS_URL} target="_blank" rel="noopener noreferrer">
              here
            </a>
            . Exclusions apply.
          </label>
        </div>
        {errors.tacAccepted && (
          <div className="re__optin--error" aria-invalid="true">
            {errors.tacAccepted.message}
          </div>
        )}
        <div
          className={clsx(
            "lander__cpl__quiz__body",
            "re__content--buttons-body"
          )}
        >
          <div
            className={clsx(
              "lander__cpl__quiz__body--question-container",
              "no-margin"
            )}
          >
            <div className="lander__cpl__quiz__body--question-actions">
              <div
                className={clsx(
                  "lander__cpl__quiz__body--button-container",
                  "no-padding-top"
                )}
              >
                <ActionButton
                  type="submit"
                  className="full-width"
                  disabled={isLoading}
                >
                  Sign me up!
                </ActionButton>
                <button
                  className={clsx(
                    "lander-quiz--button",
                    "re__content--buttons-skip"
                  )}
                  onClick={goToNextStep}
                  type="button"
                >
                  No thanks
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
